import { useMachine } from '@xstate/react'
import { AxiosError } from 'axios'
import { useCallback, useContext } from 'react'
import { useForm } from 'react-hook-form'
import { fromPromise } from 'xstate'

import { RegisterDto } from '@/domain/Authentication/dtos/register.dto'

import { CartContext } from '../../Cart.vm'

import { useAccountContext } from '@/contexts/account-context'
import {
    registerUserMachine,
    RegisterUserMachineStates,
} from './Register.machine'
import { RegisterSchema } from '@/domain/Authentication/schemas/register.schema'
import { zodResolver } from '@hookform/resolvers/zod'

export const useViewModel = () => {
    const { customer, login, register, refetchCustomer } = useAccountContext()
    const {
        cartActor: [cartState, cartSend],
    } = useContext(CartContext)

    const form = useForm<RegisterDto & { confirmPassword: string }>({
        criteriaMode: 'all',
        shouldFocusError: true,
        defaultValues: {
            first_name: '',
            last_name: '',
            email: '',
            password: '',
            confirmPassword: '',
        },
        resolver: zodResolver(RegisterSchema),
    })

    const [state, send] = useMachine(
        registerUserMachine.provide({
            actors: {
                register: fromPromise(async () => {
                    const { confirmPassword, ...dto } = form.getValues()
                    try {
                        if (!!customer) return
                        await register(dto)
                    } catch (err) {
                        if (isAxiosError(err)) {
                            if (err.response?.status === 422) {
                                // User already exists
                                send({ type: 'ERROR_USER_ALREADY_EXISTS' })
                            }
                            throw err
                        }

                        if (err instanceof Error) {
                            throw err
                        }
                    }
                }),
            },
        }),
    )

    function isAxiosError(error: any): error is AxiosError {
        return error.isAxiosError !== undefined
    }

    const validateFormByState = useCallback(
        async (state: RegisterUserMachineStates['value']) => {
            switch (state) {
                case 'general': {
                    const isEmailValid = await form.trigger('email')
                    const isPasswordValid = await form.trigger('password')
                    const isConfirmPasswordValid =
                        (await form.trigger('confirmPassword')) &&
                        form.getValues().password ===
                            form.getValues().confirmPassword

                    if (!isConfirmPasswordValid) {
                        form.setError('confirmPassword', {
                            message: 'Les mots de passe ne correspondent pas.',
                        })
                    }

                    if (
                        !isEmailValid ||
                        !isPasswordValid ||
                        !isConfirmPasswordValid
                    ) {
                        return false
                    }

                    return true
                }
                case 'personal': {
                    const isFirstNameValid = await form.trigger('first_name')
                    const isLastNameValid = await form.trigger('last_name')
                    const isPhoneValid = !!form.getValues('phone')
                        ? await form.trigger('phone')
                        : true

                    if (
                        !isFirstNameValid ||
                        !isLastNameValid ||
                        !isPhoneValid
                    ) {
                        return false
                    }

                    return true
                }
                default:
                    return true
            }
        },
        [form],
    )

    const onNext = useCallback(async () => {
        const isValid = await validateFormByState(
            state.value as RegisterUserMachineStates['value'],
        )

        if (!isValid) {
            return
        }
        send({ type: 'NEXT' })
    }, [send, state.value, validateFormByState])

    const onPrevious = useCallback(() => {
        send({ type: 'PREVIOUS' })
    }, [send])

    const onRetry = useCallback(() => {
        send({ type: 'RETRY' })
    }, [send])

    const onSignIn = () => {
        cartSend({
            type: 'AUTH.SIGN_IN',
        })
    }

    const onStartCheckout = () => {
        cartSend({
            type: 'NEXT',
        })
    }

    return {
        currentState: state.value as RegisterUserMachineStates['value'],
        onNext,
        onPrevious,
        onRetry,
        onSignIn,
        onStartCheckout,
        formControl: form.control,
    }
}
